"use client";

// Spanish footer for /es pages (DEV-15362). A slim copy of
// components/footer/footer.comp.tsx: same styles and layout, Spanish labels,
// and links that go to a Spanish page when one exists (esHref) or to the
// English page, marked EN, when it does not.
import { FaFacebookF } from "@react-icons/all-files/fa/FaFacebookF";
import { FaInstagram } from "@react-icons/all-files/fa/FaInstagram";
import { FaYoutube } from "@react-icons/all-files/fa/FaYoutube";
import { GrLinkedinOption } from "@react-icons/all-files/gr/GrLinkedinOption";
import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import Link from "next/link";
import Image from "next/image";
import cn from "classnames";
import { useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { StyledFooter } from "../footer/footer.styles";
import { CSSVars } from "@/styles/styles.config";
import { StyledDiv as StyledBigBanner } from "../bigBanner/bigBanner.styles";
import { usePathname } from "next/navigation";
import { AccordionColumn } from "../footer/accordion-column";
import { subscribeToNewsletter } from "@/helpers/newsletter";
import platforms from "../../../platforms.json";
import { IPlatform } from "../../types";
import { esHref, hasEsTwin } from "@/i18n/es/routes";
import { EnTag } from "./langSwitch.comp";

const COMPANY = [
  { name: "Sobre nosotros", url: "/about-us" },
  { name: "Funciones de la plataforma", url: "/features" },
  { name: "Contacto", url: "/contact-us" },
  { name: "Blog", url: "/blog" },
  { name: "Herramientas de marketing gratis", url: "/free-tools" },
  { name: "Centro de ayuda", url: "https://help.commoninja.com/hc/en-us", external: true },
];

const SOCIAL = [
  { href: "https://www.linkedin.com/company/common-ninja", title: "LinkedIn", icon: <GrLinkedinOption className="icon" /> },
  { href: "https://www.youtube.com/@commonninja", title: "YouTube", icon: <FaYoutube className="icon" /> },
  { href: "https://www.instagram.com/commonninja_official/", title: "Instagram", icon: <FaInstagram className="icon" /> },
  { href: "https://www.facebook.com/CommonNinja/", title: "Facebook", icon: <FaFacebookF className="icon" /> },
];

// The English footer has 6 columns on wide screens; this one has 4, so they
// share the full width instead of sitting in the first 4 of 6 narrow slots
// (long Spanish names like "Barra de cuenta regresiva" ran into the next one).
const StyledFooterEs = styled(StyledFooter)`
  @media screen and (min-width: ${CSSVars.breakpointXL}) {
    .top {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
`;

// The big banner the English footer shows on some pages (bigBannerData in
// components/footer/footer.comp.tsx), for the Spanish pages that have one.
// Same markup as components/bigBanner, plus the EN tag when the button leads
// to an English page.
const ES_BIG_BANNER: Record<
  string,
  { title: string; subtitle: string; buttonText: string; buttonLink: string; en?: boolean }
> = {
  "/es/pricing": {
    title: "Tenemos planes para empresas.",
    subtitle: "SI NINGUNO DE NUESTROS PLANES PREMIUM ES SUFICIENTE PARA TI, ¡NO TE PREOCUPES!",
    buttonText: "Contáctanos",
    buttonLink: "https://commoninja.site/custom-plan",
    en: true,
  },
};

const EnLink = ({ href, children, external }: { href: string; children: React.ReactNode; external?: boolean }) => {
  const target = esHref(href);
  const isEn = !hasEsTwin(href);
  return (
    <Link
      href={target}
      hrefLang={isEn ? "en" : "es"}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
    >
      {children}
      {isEn && <EnTag />}
    </Link>
  );
};

// `widgetNames` (slug -> Spanish name) is passed in by the server layout so the
// full Spanish page copy never ships in the browser bundle.
// `homepageLayout` matches the English footer's prop: on the home page the
// footer sits inside the closing banner's dark background.
export const FooterEs = ({
  widgetNames,
  homepageLayout = false,
}: {
  widgetNames: Record<string, string>;
  homepageLayout?: boolean;
}) => {
  const currentYear = new Date().getFullYear();
  const bigBanner = ES_BIG_BANNER[usePathname() || ""];
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  async function subscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email) {
      toast.info("Escribe tu correo electrónico.");
      return;
    }
    if (isSubscribing) return;
    setIsSubscribing(true);
    try {
      await subscribeToNewsletter(email);
      setEmail("");
      toast.success("¡Listo! Tu suscripción quedó registrada 🎉");
    } catch {
      toast.error("Algo salió mal. Inténtalo de nuevo.");
    } finally {
      setIsSubscribing(false);
    }
  }

  return (
    <StyledFooterEs
      className={cn({
        dark: true,
        "homepage-layout": homepageLayout,
        "big-banner": !!bigBanner,
      })}
      lang="es"
    >
      {bigBanner && (
        <StyledBigBanner>
          <h4 className="subtitle">{bigBanner.subtitle}</h4>
          <div className="banner">
            <h4>{bigBanner.subtitle}</h4>
            <h2>{bigBanner.title}</h2>
            <Link
              className="cta"
              href={bigBanner.buttonLink}
              hrefLang={bigBanner.en ? "en" : "es"}
            >
              {bigBanner.buttonText}
            </Link>
            {bigBanner.en && (
              <p style={{ fontSize: 12, opacity: 0.7, marginTop: 10, width: "100%", textAlign: "center" }}>
                Formulario en inglés
              </p>
            )}
          </div>
        </StyledBigBanner>
      )}
      <div className="content-wrapper">
        <div className="top">
          <div className="column">
            <Image src={"/assets/cn-logo-dark.svg"} alt="Logo de Common Ninja" width={200} height={30} />
            <div className="subscription-container">
              <p>Recibe novedades (en inglés)</p>
              <form className="input-wrapper" onSubmit={subscribe}>
                <div className="input-container">
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Tu correo electrónico"
                    type="email"
                    id="footer-email-input"
                  />
                  <button role="button" aria-label="Suscribirme" disabled={isSubscribing}>
                    <IoIosArrowForward size={16} />
                  </button>
                </div>
              </form>
            </div>
            <h2>Síguenos</h2>
            <div className="social-links">
              {SOCIAL.map((s) => (
                <Link
                  key={s.title}
                  rel="noopener noreferrer"
                  href={s.href}
                  target="_blank"
                  aria-label={`Síguenos en ${s.title}`}
                  title={s.title}
                >
                  {s.icon}
                </Link>
              ))}
            </div>
          </div>
          <AccordionColumn
            label="Widgets en español"
            links={
              <>
                {Object.entries(widgetNames).map(([slug, name]) => (
                  <Link key={slug} href={esHref(`/widgets/${slug}`)}>
                    {name}
                  </Link>
                ))}
                <Link className="bold-link" href={esHref("/widgets")}>
                  Ver todos los widgets
                  {!hasEsTwin("/widgets") && <EnTag />}
                  <IoIosArrowForward className="icon" />
                </Link>
              </>
            }
          />
          <AccordionColumn
            label="Plataformas"
            links={
              <>
                {(platforms as IPlatform[]).slice(0, 7).map((p) => (
                  <EnLink key={p.slug} href={`/platforms/${p.slug}`}>
                    {p.title}
                  </EnLink>
                ))}
                <Link className="bold-link" href={"/platforms"} hrefLang="en">
                  Ver todas las plataformas <EnTag />
                  <IoIosArrowForward className="icon" />
                </Link>
              </>
            }
          />
          <AccordionColumn
            label="Empresa"
            links={
              <>
                {COMPANY.map((l) => (
                  <EnLink key={l.url} href={l.url} external={l.external}>
                    {l.name}
                  </EnLink>
                ))}
              </>
            }
          />
        </div>
        <div className="bottom">
          <small>© Copyright 2012-{currentYear}, Common Ninja. Todos los derechos reservados.</small>
          <div className="bottom-links">
            <Link href={"/terms"} hrefLang="en">Términos y condiciones <EnTag /></Link>
            <Link href={"/privacy"} hrefLang="en">Política de privacidad <EnTag /></Link>
          </div>
        </div>
      </div>
    </StyledFooterEs>
  );
};
